import { z } from "zod";
import { DatabaseSequenceSchema, LocaleSchema, PageLimitSchema, Sha256HexSchema, UuidSchema, DecimalStringSchema, } from "./primitives.js";
export const FocusRunStateSchema = z.enum([
    "queued",
    "planning",
    "awaiting_query_review",
    "searching",
    "synthesizing",
    "cancelling",
    "cancelled",
    "failed",
    "interrupted",
    "succeeded",
]);
export const PhaseJobStateSchema = z.enum([
    "queued",
    "offered",
    "leased",
    "running",
    "cancelling",
    "cancelled",
    "succeeded",
    "failed",
    "interrupted",
]);
export const ConnectorReadinessSchema = z.enum(["unpaired", "no_grant", "offline", "model_unavailable", "ready"]);
export const FocusPhaseSchema = z.enum(["planner", "query_review", "public_search", "synthesis"]);
export const FocusToolNameSchema = z.enum([
    "session.context.read",
    "session.public.search",
    "session.proposal.stage",
]);
export const FocusRunTerminalCodeSchema = z.enum([
    "cancelled",
    "provider_unavailable",
    "provider_failed",
    "search_failed",
    "authorization_revoked",
    "interrupted",
]);
export const FocusRunProjectionSchema = z
    .object({
    id: UuidSchema,
    commandId: UuidSchema,
    workspaceId: UuidSchema,
    threadId: UuidSchema,
    branchId: UuidSchema,
    state: FocusRunStateSchema,
    phase: FocusPhaseSchema.nullable(),
    baseThreadSequence: DatabaseSequenceSchema,
    currentEventSequence: DatabaseSequenceSchema,
    queryCandidateRevision: DatabaseSequenceSchema.nullable(),
    proposalId: UuidSchema.nullable(),
    terminalCode: FocusRunTerminalCodeSchema.nullable(),
    model: z.string().min(1).max(200),
    sdkVersion: z.string().min(1).max(200),
    createdAt: z.string(),
    updatedAt: z.string(),
})
    .strict();
export const FocusQueryCandidateSchema = z
    .object({
    runId: UuidSchema,
    candidateRevision: DatabaseSequenceSchema,
    queries: z.array(z.string().min(1).max(2_048)).min(1).max(10),
    manifestDigest: Sha256HexSchema,
    state: z.enum(["pending", "approved", "rejected", "superseded"]),
})
    .strict();
export const FocusThreadCreateSchema = z
    .object({
    commandId: UuidSchema,
    threadId: UuidSchema,
    branchId: UuidSchema,
    workspaceId: UuidSchema,
    title: z.string().trim().min(1).max(200),
    branchName: z.string().trim().min(1).max(120).default("main"),
})
    .strict();
export const FocusThreadProjectionSchema = z
    .object({
    id: UuidSchema,
    workspaceId: UuidSchema,
    ownerAccountId: UuidSchema,
    title: z.string().min(1).max(200),
    revision: DatabaseSequenceSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
})
    .strict();
export const FocusBranchProjectionSchema = z
    .object({
    id: UuidSchema,
    threadId: UuidSchema,
    workspaceId: UuidSchema,
    name: z.string().min(1).max(120),
    headSequence: DatabaseSequenceSchema,
})
    .strict();
export const FocusSourceBindingSchema = z
    .object({
    sourceId: UuidSchema,
    digest: Sha256HexSchema,
    url: z.string().url().max(4_096).refine((value) => /^https?:\/\//i.test(value), "Only HTTP(S) sources are allowed"),
    title: z.string().max(1_000),
    observedAt: z.string(),
})
    .strict();
export const OmpSessionManifestSchema = z
    .object({
    manifestId: UuidSchema,
    effectiveAccountId: UuidSchema,
    branchId: UuidSchema,
    model: z.string().min(1).max(200),
    sdkVersion: z.string().min(1).max(200),
    allowedTools: z.array(FocusToolNameSchema).max(10),
    maxToolCalls: DecimalStringSchema,
    maxDecodedBytes: DecimalStringSchema,
    digest: Sha256HexSchema,
    signature: z.string().regex(/^[A-Za-z0-9_-]{86}$/),
})
    .strict();
export const FocusRunSubmitSchema = z
    .object({
    commandId: UuidSchema,
    runId: UuidSchema,
    threadId: UuidSchema,
    branchId: UuidSchema,
    parentMessageId: UuidSchema.nullable(),
    baseThreadSequence: DatabaseSequenceSchema,
    uiLocale: LocaleSchema,
    assistantLocale: LocaleSchema,
    prompt: z.string().min(1).max(262_144),
    contextManifestId: UuidSchema,
    contextManifestDigest: Sha256HexSchema,
    explicitPublicQuery: z.string().min(1).max(2_048).nullable(),
})
    .strict();
export const FocusQueryDecisionSchema = z.discriminatedUnion("decision", [
    z
        .object({
        commandId: UuidSchema,
        runId: UuidSchema,
        decision: z.literal("approve"),
        candidateRevision: DatabaseSequenceSchema,
        manifestDigest: Sha256HexSchema,
    })
        .strict(),
    z
        .object({
        commandId: UuidSchema,
        runId: UuidSchema,
        decision: z.literal("edit"),
        candidateRevision: DatabaseSequenceSchema,
        queries: z.array(z.string().trim().min(1).max(2_048)).min(1).max(10),
        manifestDigest: Sha256HexSchema,
    })
        .strict(),
    z
        .object({
        commandId: UuidSchema,
        runId: UuidSchema,
        decision: z.literal("reject"),
        candidateRevision: DatabaseSequenceSchema,
    })
        .strict(),
]);
export const FocusContextKindSchema = z.enum(["note", "task", "project", "document", "focus_message", "research_source"]);
export const FOCUS_CONTEXT_ITEM_LIMIT = 50;
export const FOCUS_MAX_DECODED_BYTES = "1048576";
export const FOCUS_MAX_TOOL_CALLS = "12";
export const FOCUS_TEXT_CONTEXT_MEDIA_TYPES = [
    "text/plain",
    "text/markdown",
    "text/x-markdown",
    "application/json",
];
export const CapabilityManifestSchema = z
    .object({
    manifestId: UuidSchema,
    attemptId: UuidSchema,
    effectiveAccountId: UuidSchema,
    workspaceIds: z.array(UuidSchema).length(1),
    contextItems: z
        .array(z
        .object({
        kind: FocusContextKindSchema,
        id: UuidSchema,
        revision: DatabaseSequenceSchema,
        versionSha256: Sha256HexSchema.nullable(),
        ranges: z.array(z.tuple([DecimalStringSchema, DecimalStringSchema])).max(100),
    })
        .strict())
        .max(FOCUS_CONTEXT_ITEM_LIMIT)
        .refine((items) => new Set(items.map(({ kind, id }) => `${kind}\0${id}`)).size === items.length, "Duplicate context items are not allowed"),
    allowedProposalOperations: z.array(z.string()).max(100),
    maxDecodedBytes: z.literal(FOCUS_MAX_DECODED_BYTES),
    maxToolCalls: z.literal(FOCUS_MAX_TOOL_CALLS),
    digest: Sha256HexSchema,
})
    .strict();
export const FocusEventFrameSchema = z
    .object({
    runId: UuidSchema,
    phaseJobId: UuidSchema,
    eventSequence: DatabaseSequenceSchema,
    payload: z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("status"), state: FocusRunStateSchema, code: z.string().nullable() }).strict(),
        z.object({ kind: z.literal("delta"), text: z.string().max(65_536) }).strict(),
        z.object({ kind: z.literal("source"), sourceId: UuidSchema, digest: Sha256HexSchema }).strict(),
    ]),
})
    .strict();
export const FocusThreadListQuerySchema = z
    .object({
    workspaceId: UuidSchema,
    cursor: UuidSchema.nullable().default(null),
    limit: PageLimitSchema.max(100).default(50),
})
    .strict();
export const FocusThreadSearchCommandSchema = FocusThreadListQuerySchema.extend({
    query: z.string().trim().min(1).max(500),
}).strict();
export const FocusThreadListSchema = z
    .object({
    items: z.array(FocusThreadProjectionSchema).max(100),
    nextCursor: UuidSchema.nullable(),
})
    .strict();
export const FocusThreadDetailSchema = z
    .object({
    thread: FocusThreadProjectionSchema,
    branches: z.array(FocusBranchProjectionSchema).max(100),
})
    .strict();
export const FocusMessageProjectionSchema = z
    .object({
    id: UuidSchema,
    workspaceId: UuidSchema,
    threadId: UuidSchema,
    branchId: UuidSchema,
    runId: UuidSchema,
    sequence: DatabaseSequenceSchema,
    role: z.enum(["user", "assistant"]),
    bodyMarkdown: z.string().max(1_048_576),
    createdByAccountId: UuidSchema,
    createdAt: z.string().datetime({ offset: true }),
})
    .strict();
export const FocusMessagePageQuerySchema = z
    .object({
    branchId: UuidSchema,
    afterSequence: DatabaseSequenceSchema.default("0"),
    limit: PageLimitSchema.max(200).default(100),
})
    .strict();
export const FocusMessagePageSchema = z
    .object({
    items: z.array(FocusMessageProjectionSchema).max(200),
    nextAfterSequence: DatabaseSequenceSchema.nullable(),
    hasMore: z.boolean(),
})
    .strict();
export const FocusRunDetailSchema = z
    .object({
    run: FocusRunProjectionSchema,
    queryCandidate: FocusQueryCandidateSchema.nullable(),
})
    .strict();
export const FocusSourceProjectionSchema = FocusSourceBindingSchema.extend({
    runId: UuidSchema,
    workspaceId: UuidSchema,
}).strict();
export const FocusReadinessProjectionSchema = z
    .object({
    state: ConnectorReadinessSchema,
    connectorId: UuidSchema.nullable(),
    model: z.string().min(1).max(200),
    observedAt: z.string().datetime({ offset: true }),
})
    .strict();
export const FocusEventPageQuerySchema = z
    .object({
    afterSequence: DatabaseSequenceSchema.default("0"),
    limit: PageLimitSchema.max(200).default(100),
})
    .strict();
export const FocusEventPageSchema = z
    .object({
    items: z.array(FocusEventFrameSchema).max(200),
    nextAfterSequence: DatabaseSequenceSchema.nullable(),
    hasMore: z.boolean(),
})
    .strict();
export const FocusRetryCommandSchema = z
    .object({
    commandId: UuidSchema,
    retryRunId: UuidSchema,
    parentMessageId: UuidSchema,
    baseThreadSequence: DatabaseSequenceSchema,
    contextManifestId: UuidSchema,
    contextManifestDigest: Sha256HexSchema,
})
    .strict();
//# sourceMappingURL=focus.js.map